import { Controller } from "@hotwired/stimulus";

// Staff combobox for the schedule quick-create modal (Week view only).
// Type-to-filter existing staff with an inline "+ Create '<typed>'" affordance.
// Maintains two hidden fields: staff_id (existing pick) and new_staff_name
// (inline create). On any selection it dispatches a bubbling
// `staff-combobox:changed` event so the service combobox can re-filter.
//
// Markup contract — see _quick_form_modal.html.erb:
//   data-controller="quick-staff-combobox"
//   data-quick-staff-combobox-options-value='[{"id":"1","name":"John"}, ...]'
//   data-quick-staff-combobox-allow-create-value="true"
//   <input data-quick-staff-combobox-target="input">
//   <input data-quick-staff-combobox-target="hiddenId" hidden>
//   <input data-quick-staff-combobox-target="newName" hidden>
//   <ul data-quick-staff-combobox-target="results"></ul>
export default class extends Controller {
  static targets = ["input", "results", "hiddenId", "newName"];
  static values = {
    options: Array,
    allowCreate: { type: Boolean, default: false },
  };

  connect() {
    this._onDocumentClick = (e) => {
      if (!this.element.contains(e.target)) this._hide();
    };
    document.addEventListener("click", this._onDocumentClick);
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick);
  }

  onInput() {
    const q = this.inputTarget.value.trim();
    this._syncHidden(q);
    this._render(q);
  }

  onFocus() {
    this._render(this.inputTarget.value.trim());
  }

  _render(q) {
    const ql = q.toLowerCase();
    const options = this.optionsValue || [];
    const matches = options.filter((o) => !ql || o.name.toLowerCase().includes(ql));
    const exact = options.some((o) => o.name.toLowerCase() === ql);
    const canCreate = this.allowCreateValue && q.length > 0 && !exact;

    this.resultsTarget.innerHTML = "";
    if (matches.length === 0 && !canCreate) {
      this._hide();
      return;
    }

    matches.forEach((o) => this.resultsTarget.appendChild(this._buildItem(o)));

    if (canCreate) {
      if (matches.length > 0) {
        const sep = document.createElement("li");
        sep.className = "border-t border-gray-200";
        sep.setAttribute("role", "separator");
        this.resultsTarget.appendChild(sep);
      }
      this.resultsTarget.appendChild(this._buildCreateItem(q));
    }

    this._show();
  }

  _buildItem(option) {
    const li = document.createElement("li");
    li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm font-medium text-black";
    li.textContent = option.name;
    li.addEventListener("click", () => this._pick(option));
    return li;
  }

  _buildCreateItem(name) {
    const li = document.createElement("li");
    li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm";
    const label = document.createElement("span");
    label.className = "text-gray-500";
    label.textContent = "+ Create ";
    const value = document.createElement("span");
    value.className = "font-medium text-black";
    value.textContent = `"${name}"`;
    li.appendChild(label);
    li.appendChild(value);
    li.addEventListener("click", () => this._pickCreate(name));
    return li;
  }

  _pick(option) {
    this.hiddenIdTarget.value = option.id;
    if (this.hasNewNameTarget) this.newNameTarget.value = "";
    this.inputTarget.value = option.name;
    this._hide();
    this._notify(option.id, option.name);
  }

  _pickCreate(name) {
    this.hiddenIdTarget.value = "";
    if (this.hasNewNameTarget) this.newNameTarget.value = name;
    this.inputTarget.value = name;
    this._hide();
    this._notify("", name);
  }

  // Keep hidden fields consistent when the operator types but submits without
  // clicking a row: exact match -> staff_id, otherwise -> new_staff_name.
  _syncHidden(q) {
    const exact = (this.optionsValue || []).find((o) => o.name.toLowerCase() === q.toLowerCase());
    if (exact) {
      this.hiddenIdTarget.value = exact.id;
      if (this.hasNewNameTarget) this.newNameTarget.value = "";
      this._notify(exact.id, exact.name);
    } else {
      this.hiddenIdTarget.value = "";
      if (this.hasNewNameTarget) this.newNameTarget.value = this.allowCreateValue ? q : "";
      this._notify("", q);
    }
  }

  _notify(id, name) {
    this.element.dispatchEvent(
      new CustomEvent("staff-combobox:changed", {
        bubbles: true,
        detail: { id: String(id || ""), name: name || "" },
      })
    );
  }

  _show() { this.resultsTarget.classList.remove("hidden"); }
  _hide() { this.resultsTarget.classList.add("hidden"); }
}
